import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ArrowRight, Clock, TrendingUp } from "lucide-react";
import { getRecentAdventure } from "../../lib/adventureApi";
import { useMarket } from "../../lib/market";

export default function RecentAdventure() {
  const [data, setData] = useState(null);
  const [playing, setPlaying] = useState(false);
  const { price } = useMarket();

  useEffect(() => { getRecentAdventure().then(setData).catch(() => {}); }, []);

  if (!data || !data.youtube_id) return null;
  const tour = data.tour;
  const poster = `https://img.youtube.com/vi/${data.youtube_id}/hqdefault.jpg`;

  return (
    <section className="bg-ink" data-testid="recent-adventure">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-20 grid lg:grid-cols-[1.35fr_1fr] gap-8 lg:gap-12 items-center">
        {/* Video */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="relative aspect-video rounded-lg overflow-hidden shadow-2xl ring-1 ring-white/10">
          {playing ? (
            <iframe
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube.com/embed/${data.youtube_id}?autoplay=1&rel=0&modestbranding=1`}
              title={data.title} allow="accelerated-autoplay; autoplay; encrypted-media; picture-in-picture"
              allowFullScreen data-testid="recent-adventure-iframe" />
          ) : (
            <button type="button" onClick={() => setPlaying(true)} data-testid="recent-adventure-play"
              className="group absolute inset-0 w-full h-full">
              <img src={poster} alt={data.title}
                className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-black/10" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-20 h-20 rounded-full bg-terracotta/95 group-hover:bg-terracotta flex items-center justify-center shadow-2xl group-hover:scale-105 transition-all">
                  <Play className="w-8 h-8 text-white ml-1" fill="currentColor" />
                </span>
              </span>
            </button>
          )}
        </motion.div>

        {/* Copy + CTA */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.08 }}>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-terracotta mb-4 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-terracotta animate-pulse" /> Recent adventure
          </p>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-white tracking-tight leading-[1.05]">
            {data.title}
          </h2>
          {data.blurb && <p className="mt-4 text-white/70 leading-relaxed max-w-md">{data.blurb}</p>}

          {tour && (
            <div className="mt-7 border-t border-white/10 pt-6">
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">The journey behind the film</p>
              <h3 className="font-display font-bold text-lg text-white leading-snug">{tour.name}</h3>
              <div className="flex items-center gap-4 mt-2 font-mono text-[11px] text-white/60">
                {tour.days > 1 && <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{tour.days} days</span>}
                {tour.difficulty && <span className="flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" />{tour.difficulty}</span>}
                {tour.price_from ? <span className="text-white/85">From <b className="text-white">{price(tour.price_from)}</b></span> : <span className="text-white/60">Price on request</span>}
              </div>
              <Link to={`/tours/${tour.slug}`} data-testid="recent-adventure-cta"
                className="mt-6 inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white pl-6 pr-5 h-12 rounded-sm font-semibold tracking-wide transition-colors">
                Explore this journey <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
